import React, {useCallback, useContext, useEffect, useMemo, useRef} from "react";
import {Col} from "reactstrap";
import {Paper} from "@mui/material";
import UserPlatformSingleEditionHomepageTopbar from "../topbar";
import {createUUId} from "../../../../../../core/services/utils/utils";
import UserHomepageControlPanel from "../../../../../containers/user-homepage/ui/control-panel";
import {UserHomepageControllerContext, UserHomepageStateContext, UserHomepageView} from "../../../../../containers/user-homepage";
import classnames from "classnames";
import {UserHomePageTypes, UserHomepageViewMode, UserHomepageWebPreviewViewportWidth} from "../../../../../containers/user-homepage/models";
import useWindowViewportWidth from "../../../../../hooks/use-window/viewport-width";
import ObserversService from "../../../../../../core/services/observers";
import ElementIds from "../../../../../../core/models/element-ids";
import debounce from "lodash/debounce";


const UserPlatformSingleEditionHomepage = ({viewType}) => {
    const state = useContext(UserHomepageStateContext);
    const controller = useContext(UserHomepageControllerContext);
    const viewportWidth = useWindowViewportWidth();
    const topbarIdRef = useRef(createUUId(true));
    const bodyIdRef = useRef(createUUId(true));
    const deviceIdRef = useRef(createUUId(true));
    const deviceContainerIdRef = useRef(createUUId(true));

    const showTabCards = useMemo(() => viewType === UserHomePageTypes.phone, [viewType])
    const controlPanelCollapsed = useMemo(() =>
            state.viewMode === UserHomepageViewMode.preview || state.controlPanel.collapsed,
        [state.viewMode, state.controlPanel.collapsed])
    const maxTabs = useMemo(() => viewType === UserHomePageTypes.phone ? 3 : 1, [viewType])

    /**
     * With each change in the viewport width:
     * - if control panel is open, or view-mode is preview, does nothing.
     * - if in widths less than and not including 'lg', opens the control panel again
     */
    useEffect(() => {
        if (!state.controlPanel.collapsed || state.viewMode === UserHomepageViewMode.preview)
            return;
        switch (viewportWidth) {
            case 'xs':
            case 'sm':
            case 'md':
                return controller.toggleControlPanelExpanded(false);
            case 'lg':
            case 'xl':
            case 'xxl':
            default:
                return;
        }
    }, [controller, state.controlPanel.collapsed, state.viewMode, viewportWidth])


    /**
     * Fetches the elements of device, topbar and panel body for calculations.
     * @return {[HTMLElement,HTMLElement]}
     */
    const getElements = () => {
        const topbar = document.getElementById(topbarIdRef.current);
        const panelBody = document.getElementById(ElementIds.panel.main);
        const panelAppbar = document.getElementById(ElementIds.panel.appBar);
        return [topbar, panelBody, panelAppbar];
    }

    /**
     * Changes the height of the view's body (control-panel and phone/web simulator)
     * @param {ResizeObserverEntry[] | Element[]} entries
     */
    const determineBodyHeight = useCallback((entries) => {
        const body = document.getElementById(bodyIdRef.current);
        if (!body)
            return;

        let topbar;
        let panelBody;
        let panelAppbar;
        for (const entry of entries) {
            switch (entry?.target?.id ?? entry?.id) {
                case topbarIdRef.current:
                    topbar = entry;
                    break;
                case ElementIds.panel.appBar:
                    panelAppbar = entry;
                    break;
                case ElementIds.panel.main:
                    panelBody = entry;
                    break;
                default:
                    break;
            }
        }

        if (!topbar)
            topbar = document.getElementById(topbarIdRef.current);
        if (!panelBody)
            panelBody = document.getElementById(ElementIds.panel.main);
        if (!panelAppbar)
            panelAppbar = document.getElementById(ElementIds.panel.appBar);

        const availableHeight =
            (panelBody?.contentRect?.height ?? panelBody.clientHeight) -
            (panelAppbar?.contentRect?.height ?? panelAppbar.scrollHeight) -
            (topbar?.contentRect?.height ?? topbar.scrollHeight) -
            48;
        body.style.maxHeight = `${availableHeight}px`
    }, [])

    /**
     * As soon as the component mounts:
     * sets an observer that determines the height of the body.
     */
    useEffect(() => {
        const [topbar, panelBody, panelAppbar] = getElements();
        if (!topbar || !panelBody || !panelAppbar)
            return;
        determineBodyHeight([topbar, panelBody, panelAppbar]);
        const observer = ObserversService.newResizeObserver(debounce(determineBodyHeight));
        ObserversService.observeResizeObserver(observer, topbar);
        ObserversService.observeResizeObserver(observer, panelBody);
        ObserversService.observeResizeObserver(observer, panelAppbar);
        return () => {
            ObserversService.unobserveResizeObserver(observer, topbar);
            ObserversService.unobserveResizeObserver(observer, panelBody);
            ObserversService.unobserveResizeObserver(observer, panelAppbar);
            ObserversService.disconnectResizeObserver(observer);
        };
    }, [determineBodyHeight]);

    /**
     * The Content of the phone homepage.
     * @type {unknown}
     */
    const content = useMemo(() => (
        <>
            <Col xs={12} id={topbarIdRef.current} className={'homepage-topbar'}>
                <UserPlatformSingleEditionHomepageTopbar/>
            </Col>
            <div id={bodyIdRef.current} className={'homepage-body'}>
                <Col
                    xs={12}
                    lg={viewType === UserHomePageTypes.phone ? 5 : 4}
                    className={classnames(
                        'homepage-control-panel order-2 order-lg-1',
                        {
                            collapsed: controlPanelCollapsed,
                        }
                    )}
                >
                    <UserHomepageControlPanel
                        showTabCards={showTabCards}
                        maxTabs={maxTabs}
                    />
                </Col>
                <Col
                    xs={12}
                    lg={viewType === UserHomePageTypes.phone ? 7 : 8}
                    id={deviceContainerIdRef.current}
                    className={classnames(
                        'homepage-platform order-1 order-lg-2',
                        {
                            [viewType]: true,
                            collapsed: controlPanelCollapsed,
                        }
                    )}
                >
                    <UserHomepageView
                        bodyId={bodyIdRef.current}
                        deviceId={deviceIdRef.current}
                        containerId={deviceContainerIdRef.current}
                        viewType={viewType}
                        viewportWidth={viewType === UserHomePageTypes.web ? UserHomepageWebPreviewViewportWidth : 'xxl'}
                    />
                </Col>
            </div>
        </>
    ), [viewType, controlPanelCollapsed, showTabCards, maxTabs])

    return (
        <>
            <div className='user-homepage-view'>
                <Paper className='paper w-100 px-3 py-2 flex-wrap justify-content-between align-items-start'>
                    {content}
                </Paper>
            </div>
        </>
    );
}

export default UserPlatformSingleEditionHomepage;
